import { Component, inject, Signal, signal, ChangeDetectionStrategy, computed } from '@angular/core';
import { YesNoPipe } from 'src/app/shared/pipes/yes-no.pipe';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EventService } from '../../services/event.service';
import {
  BreadcrumbsComponent,
  ButtonComponent,
  ColumnHeaderComponent,
  DividerComponent,
  FormFieldComponent,
  IconComponent,
  LabelComponent,
  ModalService,
  NotificationService,
  OptionComponent,
  PageHeaderComponent,
  PlaceholderComponent,
  SelectComponent,
  Table2Component
} from '@pansa/ui';
import { EventDTO, EventTypeEnum } from '../../models/event.model';
import { Router } from '@angular/router';
import { SortEvent, SortState } from '@app/shared/models/sort.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilterSortService } from '@app/shared/services/filter-sort.service';
import { tap } from 'rxjs';

@Component({
  selector: 'rqa-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    YesNoPipe,
    PageHeaderComponent,
    BreadcrumbsComponent,
    IconComponent,
    ButtonComponent,
    Table2Component,
    FormFieldComponent,
    DividerComponent,
    ColumnHeaderComponent,
    LabelComponent,
    SelectComponent,
    OptionComponent,
    PlaceholderComponent
  ]
})
export class EventListComponent {
  private eventService = inject(EventService);
  private router = inject(Router);
  private filterService = inject<FilterSortService<EventDTO>>(FilterSortService);
  private notificationService = inject(NotificationService);
  private modalService = inject(ModalService);

  filtersForm = new FormGroup({
    global: new FormControl(''),
    eventType: new FormControl('Wszystkie'),
    repeatable: new FormControl('Wszystkie')
  });

  readonly sort = signal<SortState>({
    key: 'date',
    direction: ''
  });

  filteredEvents!: Signal<EventDTO[]>;

  eventTypeOptions: string[] = ['Wszystkie', ...Object.values(EventTypeEnum)];
  minDate = new Date();
  trackBy: keyof EventDTO = 'date';
  today = new Date().toISOString().split('T')[0];

  constructor() {
    const filtersSignal = toSignal(this.filtersForm.valueChanges, {
      initialValue: this.filtersForm.value
    });

    this.filteredEvents = this.filterService.connect({
      data: this.eventService.eventsResource.value,
      filters: filtersSignal,
      sort: this.sort
    });
  }

  onAdd(): void {
    this.router.navigate(['/events/add']);
  }

  onEdit(row: EventDTO): void {
    this.router.navigate(['/events/edit/', row.id], {
      state: row
    });
  }

  onSortChange(event: SortEvent<EventDTO>): void {
    this.sort.set({
      key: event.key,
      direction: event.direction
    });
  }

  onDelete(id: number): void {
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: `Czy chcesz usunąć wydarzenie `,
      subTitle: 'Usunięcie wydarzenia jest trwałe',
      submitText: 'Usuń wydarzenie',
      cancelText: 'Anuluj',
      onSubmit: () => this.deleteEvent(id)
    });
  }

  private deleteEvent(id: number): void {
    this.eventService
      .deleteEvent(id)
      .pipe(
        tap(() => {
          this.notificationService.success({
            title: 'Sukces',
            subTitle: 'Usunięto wydarzenie'
          });
          this.eventService.refreshEvents();
        })
      )
      .subscribe();
  }
}
